import { useEffect, useRef, type ReactNode } from "react";

import type { GolDaRodada, PlacarDaRodada } from "../../game/types";
import { useT } from "../../i18n";
import { Crest } from "../../ui/atoms";
import { nomeCurto } from "../EscalacaoNoCampo";
import { BotaoDoClube } from "../FichaDoClube";

/**
 * Os outros jogos da rodada, com os goleadores.
 *
 * Duas colunas de competições no computador, para quatro séries caberem sem
 * rolar; uma embaixo da outra no celular. Cada jogo é uma linha com as siglas
 * e o placar, e os goleadores logo abaixo, cada um do lado do seu time. Um gol
 * em outro jogo acende a linha por um instante.
 */
export function PlacaresDaRodada({
  placares,
  minuto,
  encerrada,
  aoAbrirClube,
  meuClube,
}: {
  placares: PlacarDaRodada[];
  minuto: number;
  encerrada: boolean;
  aoAbrirClube?: ((clubId: string) => void) | undefined;
  meuClube: string | null;
}): ReactNode {
  const t = useT();
  const grupos: Array<{ competicao: string; jogos: PlacarDaRodada[] }> = [];
  for (const jogo of placares) {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.competicao === jogo.competicao) ultimo.jogos.push(jogo);
    else grupos.push({ competicao: jogo.competicao, jogos: [jogo] });
  }
  const colunas: Array<typeof grupos> = [[], []];
  grupos.forEach((grupo, i) => colunas[i % 2]?.push(grupo));

  return (
    <aside className="pf-rodada card" aria-label={t("aoVivo.abaRodada")}>
      <div className="pf-rodada-topo">
        <b>{t("aoVivo.abaRodada")}</b>
        <span className="label-muted num">{encerrada ? t("partida.encerrada") : t("aoVivo.minuto", { minuto })}</span>
      </div>
      <div className="scroll">
        <div className="pf-placares">
          {colunas.map((coluna, c) => (
            <div key={c} className="pf-placares-coluna">
              {coluna.map((grupo) => (
                <section key={grupo.competicao} className="pf-placares-grupo">
                  <div className="label-muted">{grupo.competicao}</div>
                  {grupo.jogos.map((jogo) => (
                    <LinhaDoJogo
                      key={jogo.fixtureId}
                      jogo={jogo}
                      abrir={(clubId) => (aoAbrirClube && clubId !== meuClube ? aoAbrirClube : undefined)}
                    />
                  ))}
                </section>
              ))}
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}

function LinhaDoJogo({
  jogo,
  abrir,
}: {
  jogo: PlacarDaRodada;
  abrir: (clubId: string) => ((clubId: string) => void) | undefined;
}): ReactNode {
  const el = useRef<HTMLDivElement | null>(null);
  const gols = jogo.gols.casa.length + jogo.gols.fora.length;
  const golsVistos = useRef(gols);

  // Gol novo acende a linha.
  useEffect(() => {
    if (gols <= golsVistos.current) {
      golsVistos.current = gols;
      return;
    }
    golsVistos.current = gols;
    const linha = el.current;
    if (!linha) return;
    linha.classList.add("piscou");
    const id = window.setTimeout(() => linha.classList.remove("piscou"), 1400);
    return () => window.clearTimeout(id);
  }, [gols]);

  return (
    <div
      ref={el}
      className="pf-placar-jogo"
      {...(jogo.minha ? { "data-minha": "" } : {})}
      title={`${jogo.homeName} ${String(jogo.homeGoals)} x ${String(jogo.awayGoals)} ${jogo.awayName}`}
    >
      <div className="pf-jogo-linha">
        <BotaoDoClube clubId={jogo.homeId} nome={jogo.homeName} aoAbrir={abrir(jogo.homeId)} className="pf-jogo-lado">
          <Crest colors={jogo.homeColors} short={jogo.homeShort} size={16} semSigla />
          <span className="pf-sigla">{jogo.homeShort}</span>
        </BotaoDoClube>
        <span className="num pf-gols">
          {jogo.homeGoals} x {jogo.awayGoals}
        </span>
        <BotaoDoClube clubId={jogo.awayId} nome={jogo.awayName} aoAbrir={abrir(jogo.awayId)} className="pf-jogo-lado dir">
          <Crest colors={jogo.awayColors} short={jogo.awayShort} size={16} semSigla />
          <span className="pf-sigla">{jogo.awayShort}</span>
        </BotaoDoClube>
      </div>
      {gols > 0 && (
        <div className="pf-goleadores">
          <Goleadores gols={jogo.gols.casa} />
          <Goleadores gols={jogo.gols.fora} />
        </div>
      )}
    </div>
  );
}

function Goleadores({ gols }: { gols: GolDaRodada[] }): ReactNode {
  const texto = gols.map((g) => `${nomeCurto(g.nome)} ${String(g.minuto)}'`).join(", ");
  return (
    <span title={texto}>
      {gols.map((g, i) => (
        <span key={`${g.nome}-${String(g.minuto)}-${String(i)}`}>
          {i > 0 && ", "}
          {nomeCurto(g.nome)} <span className="num">{g.minuto}'</span>
        </span>
      ))}
    </span>
  );
}
